/**
 * Main tracker page — map + side panel + modals + Tetris easter egg.
 * Guards the route via useAuth; polling starts on SelectionModal "Valider" or
 * automatically on mount when a saved selection exists in the cookie (§23b).
 */

import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import type { MapRef } from 'react-map-gl/maplibre';
import { useAuth } from '../hooks/useAuth';
import { usePolling } from '../hooks/usePolling';
import { useAppStore } from '../store/appStore';
import { fitToSelection } from '../utils/cameraHelpers';
import { loadAndConsumeViewport } from '../utils/sessionRestore';
import { loadSelection } from '../utils/cookieStore';
import type { MapViewport } from '../utils/sessionRestore';
import type { Flight } from '../types/api';
import Navbar from '../components/Navbar';
import MapComponent from '../components/Map';
import SidePanel from '../components/SidePanel';
import SelectionModal from '../components/SelectionModal';
import SettingsModal from '../components/SettingsModal';
import TetrisPanel from '../components/TetrisPanel';
import styles from './Tracker.module.css';

/**
 * Protected tracker page.
 * useAuth() validates the JWT and redirects to /login on expiry or absence.
 */
export default function Tracker() {
  useAuth();

  const mapRef = useRef<MapRef | null>(null);
  const { startPolling } = usePolling();

  // Consume saved viewport once on mount (before first <Map> render — §23b).
  const [initialViewport] = useState<MapViewport | null>(() => loadAndConsumeViewport());

  const [selectionOpen, setSelectionOpen] = useState(false);
  const [settingsOpen, setSettingsOpen]   = useState(false);
  const [tetrisOpen, setTetrisOpen]       = useState(false);

  // ── Restore selection from cookie on mount (§23b) ──────────────────────────
  // The selection cookie stores selectedFlightIds + per-flight prefs, but NOT
  // the Flight objects (which need lat/lon). Fetch each flight's details before
  // calling selectFlight so the map can position markers and fit bounds.
  // After restoring, start polling immediately. Do NOT call fitToSelection —
  // the map viewport is restored separately via initialViewport (sessionStorage).

  useEffect(() => {
    // Do not restore saved selection or start polling in tour demo mode.
    if (useAppStore.getState().isTourDemo) return;

    const saved = loadSelection();
    if (!saved || saved.selectedFlightIds.length === 0) return;

    const restore = async () => {
      const store = useAppStore.getState();
      for (const id of saved.selectedFlightIds) {
        try {
          const { data } = await axios.get<{ flight: Flight }>(`/flights/${id}`);
          store.selectFlight(id, data.flight);
          // Override auto-assigned prefs with the cookie-saved ones
          const savedFp = saved.perFlight[id];
          if (savedFp) {
            store.setFlightPref(id, 'showNormal',  savedFp.showNormal);
            store.setFlightPref(id, 'showInstant', savedFp.showInstant);
            store.setFlightPref(id, 'showTeams',   savedFp.showTeams);
            store.setFlightPref(id, 'isVisible',   savedFp.isVisible);
          }
        } catch {
          // Flight may have been deleted — silently skip it
        }
      }
      startPolling();
      // Do NOT call fitToSelection here — viewport is restored via initialViewport
    };

    void restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Called after SelectionModal commits; kicks off polling + fits camera. */
  const handleSelectionConfirm = () => {
    setSelectionOpen(false);
    startPolling();

    const { flights, flightPrefs, messages, teams, predictions, isPanelOpen, prefs } = useAppStore.getState();
    fitToSelection(
      Object.values(flights), flightPrefs, messages, teams,
      predictions, isPanelOpen, prefs.panelWidth, mapRef,
    );
  };

  return (
    <div className={styles.page}>
      <Navbar
        onOpenSelection={() => setSelectionOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenTetris={() => setTetrisOpen(true)}
      />

      <div className={styles.content}>
        <MapComponent mapRef={mapRef} initialViewport={initialViewport} />
        <SidePanel mapRef={mapRef} />
      </div>

      {selectionOpen && (
        <SelectionModal
          onClose={() => setSelectionOpen(false)}
          onConfirm={handleSelectionConfirm}
        />
      )}

      {settingsOpen && (
        <SettingsModal onClose={() => setSettingsOpen(false)} />
      )}

      {/* Always mounted — hidden via CSS when closed to preserve game state */}
      <TetrisPanel isOpen={tetrisOpen} onClose={() => setTetrisOpen(false)} />
    </div>
  );
}
